// shared/hooks/useLiveStats.ts
import { useState, useEffect, useCallback, useRef } from 'react';
import { useSocket } from '../../contexts/SocketContext';

export interface LiveStats {
  totalUsers: number;
  totalIncome: number;
  totalExpense: number;
  netBalance: number;
  timestamp: number;
}

export interface LiveStatsFilters {
  adminId?: string;
  accountName?: string;
  dateRange?: {
    start: string;
    end: string;
  };
}

interface UseLiveStatsReturn {
  stats: LiveStats | null;
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  isConnected: boolean;
  refreshStats: () => Promise<void>;
  clearError: () => void;
  connectionStatus: 'connected' | 'disconnected' | 'connecting' | 'error';
}

const DEFAULT_STATS: LiveStats = {
  totalUsers: 0,
  totalIncome: 0,
  totalExpense: 0,
  netBalance: 0,
  timestamp: Date.now()
};

export const useLiveStats = (filters: LiveStatsFilters = {}): UseLiveStatsReturn => {
  const { socket, isConnected, connectionStatus } = useSocket();
  const [stats, setStats] = useState<LiveStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  
  // Ref para evitar múltiples llamadas simultáneas
  const isRefreshing = useRef<boolean>(false);

  // Función para cargar estadísticas (extraída para reutilización)
  const loadStats = useCallback(async (showLoading: boolean = true) => {
    if (isRefreshing.current) {
      return;
    }
    
    const requestId = Math.random().toString(36).substr(2, 9);
    const startTime = Date.now();
    
    try {
      
      isRefreshing.current = true;
      if (showLoading) {
        setLoading(true);
      }
      setError(null);

      const queryParams = new URLSearchParams();
      
      if (filters.adminId && filters.adminId !== 'all') {
        queryParams.append('adminId', filters.adminId);
      }
      
      if (filters.accountName && filters.accountName !== 'all') {
        queryParams.append('accountName', filters.accountName);
      }
      
      if (filters.dateRange?.start && filters.dateRange?.end) {
        queryParams.append('startDate', filters.dateRange.start);
        queryParams.append('endDate', filters.dateRange.end);
      }


      const url = `/api/live-stats?${queryParams.toString()}`;
      const token = localStorage.getItem('token');
      

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });


      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [LiveStats Frontend] Error HTTP:', {
          requestId,
          status: response.status,
          statusText: response.statusText,
          errorText
        });
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success && data.data) {
        
        setStats(data.data);
        setLastUpdated(new Date());
      } else {
        console.error('❌ [LiveStats Frontend] Respuesta sin datos válidos:', {
          requestId,
          data,
          message: data.message
        });
        throw new Error(data.message || 'Error al obtener estadísticas');
      }

    } catch (err) {
      const executionTime = Date.now() - startTime;
      console.error('❌ [LiveStats Frontend] === ERROR EN CARGA ===', {
        requestId,
        error: err instanceof Error ? err.message : 'Error desconocido',
        stack: err instanceof Error ? err.stack : undefined,
        executionTimeMs: executionTime,
        filters
      });
      
      setError(err instanceof Error ? err.message : 'Error desconocido');
      setStats(DEFAULT_STATS);
    } finally {

      
      setLoading(false);
      isRefreshing.current = false;
    }
  }, [filters.adminId, filters.accountName, filters.dateRange?.start, filters.dateRange?.end]);

  // Función para refrescar estadísticas manualmente
  const refreshStats = useCallback(async (): Promise<void> => {
    isRefreshing.current = false; // Force reload
    await loadStats(true);
  }, [loadStats]);

  // Función para limpiar errores
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  // Efecto SIMPLE para cargar estadísticas cuando cambien filtros
  useEffect(() => {
    loadStats(true);
  }, [loadStats]);

  // Efecto para manejar eventos de socket
  useEffect(() => {
    
    if (!socket || !isConnected) {
      return;
    }

    // Handler para actualizaciones de estadísticas en vivo
    const handleLiveStatsUpdate = (data: any) => {
      
      if (data?.stats) {
        
        setStats(data.stats);
        setLastUpdated(new Date(data.timestamp));
        setError(null);
        
      } else {
        console.warn('⚠️ [LiveStats Frontend] liveStatsUpdate sin datos válidos:', data);
      }
    };

    // Handler para cuando se crea un usuario
    const handleUserCreated = (_: any) => {
      
      // Recargar estadísticas manteniendo los filtros actuales
      loadStats(false); // Sin mostrar loading para no interrumpir la UX
      setLastUpdated(new Date());
    };

    // Handler para cuando se crea una operación
    const handleOperationCreated = (_data: any) => {
      // ✅ La operación ya fue verificada como visible en el backend
      // Solo un delay mínimo para permitir que cualquier transacción pendiente termine
      const minimalDelay = 10;
      
      
      setTimeout(() => {
        loadStats(false);
        setLastUpdated(new Date());
      }, minimalDelay);
    };

    
    // Log global para todos los eventos de socket

    socket.onAny(() => {
    });
    
    // Registrar event listeners
    socket.on('liveStatsUpdate', handleLiveStatsUpdate);
    socket.on('userCreated', handleUserCreated);
    socket.on('operationCreated', handleOperationCreated);


    // Cleanup: remover listeners al desmontar o cambiar socket
    return () => {
      socket.off('liveStatsUpdate', handleLiveStatsUpdate);
      socket.off('userCreated', handleUserCreated);
      socket.off('operationCreated', handleOperationCreated);
      socket.offAny(); // Limpiar el listener global
    };
  }, [socket, isConnected, loadStats]);





  return {
    stats,
    loading,
    error,
    lastUpdated,
    isConnected,
    refreshStats,
    clearError,
    connectionStatus
  };
};